import React, { useCallback, useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-hot-toast";
import userService from "../../api/services/userService";

function toDateInputValue(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

const inputClass =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";
const labelClass = "block text-xs font-semibold text-gray-600 mb-1";

export default function EditUserProfileModal({ userId, isOpen, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    contactNo: "",
    gender: "",
    birthDate: "",
  });

  const loadUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setImageUrl("");
    try {
      const res = await userService.detailsUser(userId);
      const u = res?.details;
      if (!u) {
        toast.error("User not found or profile data is incomplete.");
        onClose();
        return;
      }
      setImageUrl(typeof u.imageUrl === "string" && u.imageUrl.trim() ? u.imageUrl.trim() : "");
      setForm({
        firstName: u.firstName ?? "",
        lastName: u.lastName ?? "",
        contactNo: u.contactNo ?? "",
        gender: (u.gender ?? "").toLowerCase(),
        birthDate: toDateInputValue(u.birthDate),
      });
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to load user");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [userId, onClose]);

  useEffect(() => {
    if (!isOpen || !userId) return;
    loadUser();
  }, [isOpen, userId, loadUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) return;
    if (!form.firstName.trim() || !form.lastName.trim() || !form.contactNo.trim()) {
      toast.error("First name, last name, and contact number are required. Please fill all the fields.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        contactNo: form.contactNo.trim() || undefined,
        gender: form.gender || undefined,
        birthDate: form.birthDate || undefined,
      };

      Object.keys(body).forEach((k) => {
        if (body[k] === undefined) delete body[k];
      });

      const res = await userService.updateAppUserDetails(userId, body);
      if (res?.responseCode === 200 || res?.success) {
        toast.success(res?.message || "User updated successfully");
        onSaved?.();
        onClose();
      } else {
        toast.error(res?.message || "Update failed");
      }
    } catch (err) {
      toast.error(err?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-user-title">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h2 id="edit-user-title" className="text-lg font-bold text-gray-900">
            Edit user #{userId}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            aria-label="Close">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-500 border-t-transparent" />
            <p className="mt-4 text-gray-600 text-sm">Loading user…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
            <p className="text-xs text-gray-500">
              Only these fields can be edited from the admin panel. Optional fields left empty are not sent (existing
              values are kept).
            </p>

            {imageUrl ? (
              <div className="flex flex-col items-center sm:items-start gap-2">
                <span className={labelClass}>Profile picture</span>
                <img
                  src={imageUrl}
                  alt=""
                  className="h-24 w-24 rounded-full object-cover border-2 border-gray-200 shadow-sm bg-gray-50"
                />
              </div>
            ) : null}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>First name</label>
                <input name="firstName" value={form.firstName} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Last name</label>
                <input name="lastName" value={form.lastName} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Gender</label>
                <select name="gender" value={form.gender} onChange={handleChange} className={inputClass}>
                  <option value="">—</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Contact</label>
                <input name="contactNo" value={form.contactNo} onChange={handleChange} className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Birth date</label>
                <input name="birthDate" type="date" value={form.birthDate} onChange={handleChange} className={inputClass} />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2 pb-2 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-50">
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
